import * as Common from "DistributedTaskControls/Common/Common";
import { SourceBase } from "DistributedTaskControls/Common/Sources/SourceBase";
import { SourceManager } from "DistributedTaskControls/Common/Sources/SourceManager";
import { BuildLegacyHttpClient } from "DistributedTaskControls/Sources/BuildLegacyHttpClient";
import { ConnectedServiceMetadata } from "DistributedTasksCommon/TFS.Tasks.Types";

import { AuthRequest } from "TFS/Admin/Scripts/Generated/ConnectedService";
import { ConnectedServiceHttpClient } from "TFS/Admin/Scripts/Generated/ConnectedServiceHttpClient";
import { AzureSubscriptionQueryResult, TaskDefinitionEndpoint } from "TFS/DistributedTask/Contracts";
import { TaskAgentHttpClient } from "TFS/DistributedTask/TaskAgentRestClient";
import { ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointRequestResult, ServiceEndpointType, AzureManagementGroupQueryResult, ServiceEndpointProjectReference } from "TFS/ServiceEndpoint/Contracts";
import { ServiceEndpointHttpClient } from "TFS/ServiceEndpoint/ServiceEndpointRestClient";
import * as VssContext from "VSS/Context";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { getClient, getCollectionClient } from "VSS/Service";


export class ConnectedServiceEndpointSource extends SourceBase {

    public static getKey(): string {
        return "ConnectedServiceEndpointSource";
    }

    constructor() {
        super();
        this._dtAgentClient = getCollectionClient(TaskAgentHttpClient);
        this._connectedServiceHttpClient = getClient(ConnectedServiceHttpClient);
        this._serviceEndpointHttpClient = getClient(ServiceEndpointHttpClient);
    }

    public createAuthRequest(connectedServiceId: string): IPromise<AuthRequest> {
        let projectName = VssContext.getDefaultWebContext().project.name;
        const isBitbucketConnection = connectedServiceId.toLowerCase() === Common.ServiceEndpointType.Bitbucket;
        let configurationId = undefined;
        if (isBitbucketConnection) {
            const useNewOAuthApp = FeatureAvailabilityService.isFeatureEnabled(Common.FeatureFlag_BitbucketAzurePipelinesOAuthClient, false);
            if (useNewOAuthApp) {
                configurationId = FeatureAvailabilityService.isFeatureEnabled(Common.FeatureFlag_BitbucketAzurePipelinesBackupOAuthClient, false)
                    ? Common.BitbucketAzurePipelinesBackupOAuthAppId
                    : Common.BitbucketAzurePipelinesOAuthAppId;
            }
        }

        return this._connectedServiceHttpClient.createAuthRequest(undefined, projectName, connectedServiceId, configurationId);
    }

    public createServiceEndpoint(serviceEndPoint: ServiceEndpoint): IPromise<ServiceEndpoint> {
        let projectId = VssContext.getDefaultWebContext().project.id;
        this.fillServiceEndpointProjectReferencesForEndpoint(serviceEndPoint, projectId);
        return this._serviceEndpointHttpClient.createServiceEndpoint(serviceEndPoint);
    }

    public updateServiceEndpoint(serviceEndPoint: ServiceEndpoint): IPromise<ServiceEndpoint> {
        let projectId = VssContext.getDefaultWebContext().project.id;
        this.fillServiceEndpointProjectReferencesForEndpoint(serviceEndPoint, projectId);
        return this._serviceEndpointHttpClient.updateServiceEndpoint(serviceEndPoint, serviceEndPoint.id, null);
    }

    public getServiceEndpoints(connectedServiceType: string, authSchemes?: string[], endpointIds?: string[]): IPromise<ServiceEndpoint[]> {
        let projectName = VssContext.getDefaultWebContext().project.name;
        return this._serviceEndpointHttpClient.getServiceEndpoints(projectName, connectedServiceType, authSchemes, endpointIds);
    }

    public getServiceEndpointTypes(connectedServiceType?: string, scheme?: string): IPromise<ServiceEndpointType[]> {
        return this._serviceEndpointHttpClient.getServiceEndpointTypes(connectedServiceType, scheme);
    }

    public getServiceEndpoint(serviceEndpointId: string): IPromise<ServiceEndpoint> {
        let projectName = VssContext.getDefaultWebContext().project.name;
        return this._serviceEndpointHttpClient.getServiceEndpointDetails(projectName, serviceEndpointId);
    }

    public executeServiceEndpointRequest(serviceEndpointRequest: ServiceEndpointRequest, endpointId: string): IPromise<ServiceEndpointRequestResult> {
        let projectId = VssContext.getDefaultWebContext().project.id;
        return this._serviceEndpointHttpClient.executeServiceEndpointRequest(serviceEndpointRequest, projectId, endpointId);
    }

    public queryEndpoint(taskEndpoint: TaskDefinitionEndpoint): IPromise<string[]> {
        let projectId = VssContext.getDefaultWebContext().project.id;
        taskEndpoint.scope = projectId;
        return this._dtAgentClient.queryEndpoint(taskEndpoint);
    }

    public beginGetAzureSubscriptions(): IPromise<AzureSubscriptionQueryResult> {
        return this._dtAgentClient.getAzureSubscriptions();
    }

    public beginGetAzureManagementGroups(): IPromise<AzureManagementGroupQueryResult> {
        return this._dtAgentClient.getAzureManagementGroups();
    }

    /**
     * Gets connected services subscriptions for the azureConnection task input type  - This is to keep support for compat scenario : old tasks with new server OM for "Build" hub
     */
    public beginGetSubscriptionNames(): IPromise<ConnectedServiceMetadata[]> {
        let projectName = VssContext.getDefaultWebContext().project.name;
        return this._buildLegacyHttpClient.beginGetSubscriptionNames(projectName);
    }

    public static instance(): ConnectedServiceEndpointSource {
        return SourceManager.getSource(ConnectedServiceEndpointSource);
    }

    private fillServiceEndpointProjectReferencesForEndpoint(endpoint: ServiceEndpoint, projectId: string): void {
        let projectReference: ServiceEndpointProjectReference = {
            name: endpoint.name,
            description: endpoint.description,
            projectReference: {
                id: projectId,
                name: ""
            }
        };

        endpoint.serviceEndpointProjectReferences = [projectReference];
    }

    private _dtAgentClient: TaskAgentHttpClient;
    private _connectedServiceHttpClient: ConnectedServiceHttpClient;
    private _buildLegacyHttpClient: BuildLegacyHttpClient;
    private _serviceEndpointHttpClient: ServiceEndpointHttpClient;
}